import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Check, Loader2, LogIn, MapPin, Plus } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useCart } from '@/context/CartContext';
import { useCustomer } from '@/context/CustomerContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { customer, isAuthenticated, loading: authLoading, token } = useCustomer();
  const total = getCartTotal();

  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [notes, setNotes] = useState('');
  const [newAddress, setNewAddress] = useState({
    label: 'Home',
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    is_default: false
  });

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast.error('Please login to checkout');
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch saved addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      if (isAuthenticated && token) {
        try {
          const response = await axios.get(`${API}/customer/addresses`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAddresses(response.data.addresses || []);
          setSelectedAddressId(response.data.default_address_id);
          
          // Show form if no addresses
          if (!response.data.addresses || response.data.addresses.length === 0) {
            setShowNewAddressForm(true);
            // Pre-fill name and phone from customer
            setNewAddress(prev => ({
              ...prev,
              name: customer?.name || '',
              phone: customer?.phone || ''
            }));
          }
        } catch (error) {
          console.error('Error fetching addresses:', error);
        } finally {
          setLoadingAddresses(false);
        }
      }
    };
    fetchAddresses();
  }, [isAuthenticated, token, customer]);

  const handleNewAddressChange = (e) => {
    setNewAddress(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleAddAddress = async () => {
    if (!newAddress.name || !newAddress.phone || !newAddress.address || !newAddress.city || !newAddress.state || !newAddress.pincode) {
      toast.error('Please fill all address fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/customer/addresses`, newAddress, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const addedAddress = response.data.address;
      setAddresses(prev => [...prev, addedAddress]);
      setSelectedAddressId(addedAddress.id);
      setShowNewAddressForm(false);
      setNewAddress({
        label: 'Home',
        name: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        is_default: false
      });
      toast.success('Address added successfully');
    } catch (error) {
      toast.error('Failed to add address');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!selectedAddressId && !showNewAddressForm) {
      toast.error('Please select or add a delivery address');
      return;
    }

    // If adding new address during checkout
    if (showNewAddressForm) {
      if (!newAddress.name || !newAddress.phone || !newAddress.address || !newAddress.city || !newAddress.state || !newAddress.pincode) {
        toast.error('Please fill all address fields');
        return;
      }
    }

    setLoading(true);

    try {
      // If new address form is shown, add it first
      let shippingAddress;
      if (showNewAddressForm) {
        const addressResponse = await axios.post(`${API}/customer/addresses`, {
          ...newAddress,
          is_default: addresses.length === 0
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        shippingAddress = addressResponse.data.address;
      } else {
        shippingAddress = addresses.find(addr => addr.id === selectedAddressId);
      }

      if (!shippingAddress) {
        toast.error('Please select a delivery address');
        setLoading(false);
        return;
      }

      const orderData = {
        customer: {
          name: shippingAddress.name,
          email: customer.email,
          phone: shippingAddress.phone,
          address: shippingAddress.address,
          city: shippingAddress.city,
          state: shippingAddress.state,
          pincode: shippingAddress.pincode
        },
        items: cartItems.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          is_custom: item.customOptions?.isCustom || false,
          custom_notes: item.customOptions?.notes || null,
          custom_preferences: item.customOptions?.preferences || null,
          custom_image: item.customOptions?.image || null
        })),
        notes: notes || null
      };

      const response = await axios.post(`${API}/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      clearCart();
      navigate(`/order-confirmation/${response.data.id}`, { state: { order: response.data } });
    } catch (error) {
      console.error('Order error:', error);
      if (error.response?.status === 401) {
        toast.error('Please login to place order');
        navigate('/login', { state: { from: { pathname: '/checkout' } } });
      } else {
        toast.error('Failed to place order. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  // Redirect handled by useEffect, but show login prompt as fallback
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen section-spacing">
        <div className="container-handcraft text-center">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
              <LogIn className="w-10 h-10 text-accent" />
            </div>
            <p className="font-handwritten text-3xl text-accent mb-4">Hold On!</p>
            <h1 className="font-serif text-3xl text-charcoal mb-4">Login Required</h1>
            <p className="text-muted-foreground mb-8">
              Please login or create an account to proceed with checkout.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login" state={{ from: { pathname: '/checkout' } }}>
                <Button className="btn-handcraft bg-charcoal text-cream w-full sm:w-auto">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="outline" className="btn-handcraft w-full sm:w-auto">
                  Create Account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen section-spacing">
        <div className="container-handcraft text-center">
          <p className="font-handwritten text-3xl text-accent mb-4">Oops!</p>
          <h1 className="font-serif text-3xl text-charcoal mb-4">Your cart is empty</h1>
          <Link to="/shop">
            <Button className="btn-handcraft bg-charcoal text-cream">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-sand">
        <div className="container-handcraft">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-charcoal transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/cart" className="hover:text-charcoal transition-colors">Cart</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-charcoal">Checkout</span>
          </nav>
        </div>
      </div>

      <section className="section-spacing">
        <div className="container-handcraft">
          <div className="text-center mb-12">
            <p className="font-handwritten text-2xl text-accent mb-2">Almost There</p>
            <h1 className="font-serif text-3xl md:text-4xl text-charcoal">Checkout</h1>
          </div>

          {/* Logged in user info */}
          <div className="bg-sage/10 rounded-xl p-4 md:p-6 mb-8 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center">
              <Check className="w-5 h-5 text-sage" />
            </div>
            <div>
              <p className="font-medium text-charcoal">Logged in as {customer?.name}</p>
              <p className="text-sm text-muted-foreground">{customer?.email}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
              {/* Left Column - Addresses */}
              <div className="lg:col-span-2 space-y-8">
                {/* Delivery Address */}
                <div className="bg-white rounded-2xl border border-sand p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-serif text-xl text-charcoal">Delivery Address</h2>
                    {addresses.length > 0 && !showNewAddressForm && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowNewAddressForm(true)}
                        data-testid="add-new-address"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add New
                      </Button>
                    )}
                  </div>

                  {loadingAddresses ? (
                    <div className="space-y-4">
                      {[...Array(2)].map((_, i) => (
                        <div key={i} className="h-24 skeleton rounded-xl" />
                      ))}
                    </div>
                  ) : (
                    <>
                      {/* Saved Addresses */}
                      {addresses.length > 0 && !showNewAddressForm && (
                        <RadioGroup
                          value={selectedAddressId}
                          onValueChange={setSelectedAddressId}
                          className="space-y-4"
                        >
                          {addresses.map((address) => (
                            <div key={address.id} className="relative">
                              <RadioGroupItem
                                value={address.id}
                                id={address.id}
                                className="peer sr-only"
                              />
                              <Label
                                htmlFor={address.id}
                                className="flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all peer-data-[state=checked]:border-accent peer-data-[state=checked]:bg-accent/5 hover:border-accent/50"
                                data-testid={`address-${address.id}`}
                              >
                                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                                  <MapPin className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-charcoal">{address.name}</span>
                                    <span className="badge-handcraft bg-muted text-muted-foreground text-xs">
                                      {address.label}
                                    </span>
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {address.address}, {address.city}, {address.state} - {address.pincode}
                                  </p>
                                  <p className="text-sm text-muted-foreground">{address.phone}</p>
                                </div>
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      )}

                      {/* New Address Form */}
                      {showNewAddressForm && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <Label className="label-handcraft">Full Name *</Label>
                              <Input
                                name="name"
                                value={newAddress.name}
                                onChange={handleNewAddressChange}
                                required
                                placeholder="John Doe"
                                data-testid="address-name"
                              />
                            </div>
                            <div>
                              <Label className="label-handcraft">Phone *</Label>
                              <Input
                                name="phone"
                                value={newAddress.phone}
                                onChange={handleNewAddressChange}
                                required
                                placeholder="+91 98765 43210"
                                data-testid="address-phone"
                              />
                            </div>
                          </div>

                          <div>
                            <Label className="label-handcraft">Address *</Label>
                            <Textarea
                              name="address"
                              value={newAddress.address}
                              onChange={handleNewAddressChange}
                              required
                              placeholder="House/Flat No., Building, Street, Area"
                              data-testid="address-address"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <Label className="label-handcraft">City *</Label>
                              <Input
                                name="city"
                                value={newAddress.city}
                                onChange={handleNewAddressChange}
                                required
                                placeholder="Mumbai"
                                data-testid="address-city"
                              />
                            </div>
                            <div>
                              <Label className="label-handcraft">State *</Label>
                              <Input
                                name="state"
                                value={newAddress.state}
                                onChange={handleNewAddressChange}
                                required
                                placeholder="Maharashtra"
                                data-testid="address-state"
                              />
                            </div>
                            <div>
                              <Label className="label-handcraft">Pincode *</Label>
                              <Input
                                name="pincode"
                                value={newAddress.pincode}
                                onChange={handleNewAddressChange}
                                required
                                placeholder="400001"
                                data-testid="address-pincode"
                              />
                            </div>
                          </div>

                          <div>
                            <Label className="label-handcraft">Address Label</Label>
                            <div className="flex gap-2 mt-2">
                              {['Home', 'Work', 'Other'].map((label) => (
                                <Button
                                  key={label}
                                  type="button"
                                  variant={newAddress.label === label ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setNewAddress(prev => ({ ...prev, label }))}
                                  className={newAddress.label === label ? 'bg-charcoal text-cream' : ''}
                                >
                                  {label}
                                </Button>
                              ))}
                            </div>
                          </div>

                          {addresses.length > 0 && (
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowNewAddressForm(false)}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                onClick={handleAddAddress}
                                className="bg-charcoal text-cream"
                              >
                                Save Address
                              </Button>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </>
                  )}
                </div>

                {/* Order Notes */}
                <div className="bg-white rounded-2xl border border-sand p-6 md:p-8">
                  <h2 className="font-serif text-xl text-charcoal mb-6">Additional Notes</h2>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any special instructions for your order..."
                    className="min-h-[100px]"
                    data-testid="checkout-notes"
                  />
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 bg-white rounded-2xl border border-sand p-6 md:p-8">
                  <h2 className="font-serif text-xl text-charcoal mb-6">Order Summary</h2>
                  
                  <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                    {cartItems.map((item, index) => (
                      <div key={index} className="flex gap-3">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                          <img
                            src={item.product.images?.[0] || 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200'}
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-charcoal truncate">
                            {item.product.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Qty: {item.quantity}
                          </p>
                          {item.customOptions?.isCustom && (
                            <span className="badge-handcraft bg-sage/20 text-sage text-xs mt-1">
                              Custom
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium">
                          ₹{((item.product.sale_price || item.product.price) * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-sand pt-4 space-y-3 mb-6">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Subtotal</span>
                      <span>₹{total.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Shipping</span>
                      <span className="text-sage">Free</span>
                    </div>
                    <div className="flex justify-between text-lg font-semibold text-charcoal pt-2 border-t border-sand">
                      <span>Total</span>
                      <span>₹{total.toLocaleString()}</span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || (!selectedAddressId && !showNewAddressForm)}
                    className="w-full btn-handcraft bg-charcoal text-cream hover:bg-charcoal/90"
                    data-testid="place-order"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Place Order
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground mt-4">
                    By placing this order, you agree to our terms and conditions.
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default CheckoutPage;
